import React, { useState } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';

const AdminCouncilVoteEntry = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    meeting_date: '',
    motion_title: '',
    motion_result: '',
    pdf_link: '',
    votes_yes: [],
    votes_no: [],
    votes_abstain: []
  });

  const COUNCILLORS = [
    'Mayor Sandra Masters',
    'Andrew Stevens (Ward 1)',
    'Bob Hawkins (Ward 2)',
    'Andrew Stevens (Ward 3)',
    'Lori Bresciani (Ward 4)',
    'John Findura (Ward 5)',
    'Dan LeBlanc (Ward 6)',
    'Terina Shaw (Ward 7)',
    'Shanon Zachidniak (Ward 8)',
    'Jason Mancinelli (Ward 9)',
    'Landon Mohl (Ward 10)'
  ];

  const handleVoteChange = (councillor, voteType) => {
    const newData = { ...formData };
    
    newData.votes_yes = newData.votes_yes.filter(c => c !== councillor);
    newData.votes_no = newData.votes_no.filter(c => c !== councillor);
    newData.votes_abstain = newData.votes_abstain.filter(c => c !== councillor);
    
    if (voteType === 'yes') newData.votes_yes.push(councillor);
    if (voteType === 'no') newData.votes_no.push(councillor);
    if (voteType === 'abstain') newData.votes_abstain.push(councillor);
    
    setFormData(newData);
  };

  const getVoteType = (councillor) => {
    if (formData.votes_yes.includes(councillor)) return 'yes';
    if (formData.votes_no.includes(councillor)) return 'no';
    if (formData.votes_abstain.includes(councillor)) return 'abstain';
    return 'none';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.meeting_date || !formData.motion_title) {
      toast.error('Meeting date and motion title required');
      return;
    }
    if (!formData.motion_result) {
      toast.error('Select a result (CARRIED or DEFEATED)');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/votes', {
        date: formData.meeting_date,
        motion: formData.motion_title,
        result: formData.motion_result,
        yes_votes: formData.votes_yes,
        no_votes: formData.votes_no,
        abstained_votes: formData.votes_abstain,
        not_present_votes: [],
        conflict_votes: [],
        pdf_link: formData.pdf_link || null
      });
      
      toast.success('Vote recorded');
      setFormData({
        meeting_date: '',
        motion_title: '',
        motion_result: '',
        pdf_link: '',
        votes_yes: [],
        votes_no: [],
        votes_abstain: []
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" />;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold mb-8">Manual Council Vote Entry</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-mono text-gray-400 mb-2">Meeting Date</label>
              <input
                type="date"
                value={formData.meeting_date}
                onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-gray-800 text-white px-4 py-2 rounded"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-mono text-gray-400 mb-2">Motion Title</label>
              <input
                type="text"
                value={formData.motion_title}
                onChange={(e) => setFormData({ ...formData, motion_title: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-gray-800 text-white px-4 py-2 rounded"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-mono text-gray-400 mb-2">Result</label>
              <select
                value={formData.motion_result}
                onChange={(e) => setFormData({ ...formData, motion_result: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-gray-800 text-white px-4 py-2 rounded"
                required
              >
                <option value="">-- Select --</option>
                <option value="CARRIED">CARRIED</option>
                <option value="DEFEATED">DEFEATED</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-mono text-gray-400 mb-2">PDF Link (optional)</label>
              <input
                type="url"
                value={formData.pdf_link}
                onChange={(e) => setFormData({ ...formData, pdf_link: e.target.value })}
                placeholder="https://..."
                className="w-full bg-[#1a1a1a] border border-gray-800 text-white px-4 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-mono text-gray-400 mb-4">Votes</label>
              <div className="space-y-3">
                {COUNCILLORS.map(councillor => {
                  const vote = getVoteType(councillor);
                  return (
                    <div key={councillor} className="flex items-center justify-between bg-[#1a1a1a] border border-gray-800 p-3 rounded">
                      <span className="text-sm">{councillor}</span>
                      <div className="flex gap-2">
                        {['yes', 'no', 'abstain', 'none'].map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => handleVoteChange(councillor, type)}
                            className={`px-3 py-1 text-xs font-mono rounded ${
                              vote === type 
                                ? type === 'yes' ? 'bg-green-600' : type === 'no' ? 'bg-red-600' : type === 'abstain' ? 'bg-yellow-600' : 'bg-gray-600'
                                : 'bg-gray-800 text-gray-400'
                            } ${vote === type ? 'text-white' : ''}`}
                          >
                            {type.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A52A] hover:bg-yellow-500 text-black font-mono py-3 rounded disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminCouncilVoteEntry;